import { useMemo, useState } from 'react';

import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react';

import type { TopUser } from '@/types/service/internalAnalytics';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { formatCurrency, formatNumber } from '@/utils/saasDashboardHelpers';

interface LynAITopUsersTableProps {
  users: TopUser[];
}

type SortField =
  | 'principal_name'
  | 'client_name'
  | 'total_prompts'
  | 'total_tokens'
  | 'total_cost_usd';
type SortDirection = 'asc' | 'desc' | null;

interface SortState {
  field: SortField | null;
  direction: SortDirection;
}

const LynAITopUsersTable = ({ users }: LynAITopUsersTableProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortState, setSortState] = useState<SortState>({ field: null, direction: null });

  const handleSort = (field: SortField) => {
    setSortState((prev) => {
      if (prev.field !== field) {
        return { field, direction: 'asc' };
      }
      if (prev.direction === 'asc') {
        return { field, direction: 'desc' };
      }
      if (prev.direction === 'desc') {
        return { field: null, direction: null };
      }
      return { field, direction: 'asc' };
    });
  };

  const getSortIcon = (field: SortField) => {
    if (sortState.field !== field) {
      return <ArrowUpDown className='ml-1 h-4 w-4 text-muted-foreground' />;
    }
    if (sortState.direction === 'asc') {
      return <ArrowUp className='ml-1 h-4 w-4' />;
    }
    return <ArrowDown className='ml-1 h-4 w-4' />;
  };

  const filteredAndSortedUsers = useMemo(() => {
    let result = [...users];

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((user) => {
        return (
          user.principal_name?.toLowerCase().includes(query) ||
          user.principal_id?.toLowerCase().includes(query) ||
          user.email_address?.toLowerCase().includes(query) ||
          user.client_name.toLowerCase().includes(query)
        );
      });
    }

    // Sort
    if (sortState.field && sortState.direction) {
      result.sort((a, b) => {
        let aVal: string | number;
        let bVal: string | number;

        switch (sortState.field) {
          case 'principal_name':
            aVal = a.principal_name || a.principal_id || '';
            bVal = b.principal_name || b.principal_id || '';
            break;
          case 'client_name':
            aVal = a.client_name;
            bVal = b.client_name;
            break;
          case 'total_prompts':
            aVal = a.total_prompts;
            bVal = b.total_prompts;
            break;
          case 'total_tokens':
            aVal = a.total_tokens;
            bVal = b.total_tokens;
            break;
          case 'total_cost_usd':
            aVal = parseFloat(a.total_cost_usd);
            bVal = parseFloat(b.total_cost_usd);
            break;
          default:
            return 0;
        }

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortState.direction === 'asc' ? aVal - bVal : bVal - aVal;
        }

        const comparison = String(aVal).localeCompare(String(bVal));
        return sortState.direction === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [users, searchQuery, sortState]);

  if (users.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='text-lg'>Top Users by Token Usage</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='h-[200px] flex items-center justify-center text-muted-foreground'>
            No user data available
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
          <CardTitle className='text-lg'>
            Top Users
            <span className='ml-2 text-sm font-normal text-muted-foreground'>
              ({filteredAndSortedUsers.length} of {users.length})
            </span>
          </CardTitle>
          <div className='relative w-full sm:w-64'>
            <Search className='absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground' />
            <Input
              type='text'
              placeholder='Search users...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className='pl-8'
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className='h-[400px]'>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead
                  className='cursor-pointer hover:bg-muted/50 select-none'
                  onClick={() => handleSort('principal_name')}
                >
                  <div className='flex items-center'>
                    User
                    {getSortIcon('principal_name')}
                  </div>
                </TableHead>
                <TableHead
                  className='cursor-pointer hover:bg-muted/50 select-none'
                  onClick={() => handleSort('client_name')}
                >
                  <div className='flex items-center'>
                    Client
                    {getSortIcon('client_name')}
                  </div>
                </TableHead>
                <TableHead
                  className='cursor-pointer hover:bg-muted/50 select-none text-right'
                  onClick={() => handleSort('total_prompts')}
                >
                  <div className='flex items-center justify-end'>
                    Prompts
                    {getSortIcon('total_prompts')}
                  </div>
                </TableHead>
                <TableHead
                  className='cursor-pointer hover:bg-muted/50 select-none text-right'
                  onClick={() => handleSort('total_tokens')}
                >
                  <div className='flex items-center justify-end'>
                    Tokens
                    {getSortIcon('total_tokens')}
                  </div>
                </TableHead>
                <TableHead className='text-right hidden md:table-cell'>Input / Output</TableHead>
                <TableHead
                  className='cursor-pointer hover:bg-muted/50 select-none text-right'
                  onClick={() => handleSort('total_cost_usd')}
                >
                  <div className='flex items-center justify-end'>
                    Cost
                    {getSortIcon('total_cost_usd')}
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className='h-24 text-center text-muted-foreground'>
                    No users match your search.
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSortedUsers.map((user, index) => (
                  <TableRow key={`${user.principal_id}-${index}`}>
                    <TableCell>
                      <div>
                        <div className='font-medium'>
                          {user.principal_name || user.principal_id || 'Unknown'}
                        </div>
                        {user.email_address && (
                          <div className='text-xs text-muted-foreground truncate max-w-[200px]'>
                            {user.email_address}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{user.client_name}</TableCell>
                    <TableCell className='text-right'>{formatNumber(user.total_prompts)}</TableCell>
                    <TableCell className='text-right'>{formatNumber(user.total_tokens)}</TableCell>
                    <TableCell className='text-right text-xs text-muted-foreground hidden md:table-cell'>
                      {formatNumber(user.total_input_tokens)} /{' '}
                      {formatNumber(user.total_output_tokens)}
                    </TableCell>
                    <TableCell className='text-right font-medium'>
                      {formatCurrency(parseFloat(user.total_cost_usd))}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default LynAITopUsersTable;
